import { useEffect, useState } from 'react';
import { DateTimeDisplay, StatusMessage, Breadcrumbs } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import { ModalContent } from '../../components/ui/ModalContent';
import { TOAST_ERROR_MS } from '../../constants/timeouts';
import { AttachmentGroupEditor } from '../../features/attachments/AttachmentGroupEditor';
import { AttachmentGroupList } from '../../features/attachments/AttachmentGroupList';
import { useAttachmentGroups } from '../../features/attachments/useAttachmentGroups';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import type { AttachmentGroupDto } from '../../services/attachments/attachment.types';

const AttachmentGroups = () => {
    const { groups, loading, error, actionInFlight, refresh, loadForEdit, remove, clearError } = useAttachmentGroups();
    const [editor, setEditor] = useState<{ group: AttachmentGroupDto | null } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AttachmentGroupDto | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState<{ message: string; autoDismiss: boolean } | null>(null);
    const breadcrumbs = useBreadcrumbs({ currentPageTitle: 'Attachment Groups' });

    useEffect(() => {
        if (!success?.autoDismiss) return;
        const timeout = setTimeout(() => setSuccess(null), TOAST_ERROR_MS);
        return () => clearTimeout(timeout);
    }, [success]);

    const handleEdit = async (group: AttachmentGroupDto) => {
        const loadedGroup = await loadForEdit(group);
        if (loadedGroup) setEditor({ group: loadedGroup });
    };

    const handleSaved = () => {
        setSuccess({
            message: editor?.group ? 'Attachment group updated' : 'Attachment group created',
            autoDismiss: true,
        });
        setEditor(null);
        void refresh(true);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        if (await remove(target)) {
            setSuccess({ message: 'Attachment group deleted', autoDismiss: false });
        }
    };

    return (
        <main>
            <div className="container mb-2">
                <section className="page">
                    <div className="heading__section">
                        <div className="heading__title-group">
                            <h2 className="heading__title heading__title--attachment-groups">Attachment Groups</h2>
                            <DateTimeDisplay />
                            <Breadcrumbs items={breadcrumbs} />
                        </div>
                        <div>
                            {!editor && (
                                <button type="button" className="btn btn-add-new" onClick={() => setEditor({ group: null })}>
                                    Add Attachment Group
                                </button>
                            )}
                        </div>
                    </div>
                </section>

                {submitting && <p>Saving attachment group…</p>}
                {success && (
                    <StatusMessage kind="success" onClose={() => setSuccess(null)}>
                        {success.message}
                    </StatusMessage>
                )}

                <section className="page">
                    <div className="alarm-content">
                        <AttachmentGroupList
                            groups={groups}
                            loading={loading}
                            error={error}
                            onDismissError={clearError}
                            editing={Boolean(editor?.group)}
                            actionInFlight={actionInFlight}
                            onEdit={group => { void handleEdit(group); }}
                            onDelete={setDeleteTarget}
                        />
                    </div>
                </section>

                <Modal
                    isOpen={deleteTarget !== null}
                    title="Delete Attachment Group"
                    headerClass="modal__header--danger"
                    content={<ModalContent heading="Delete Attachment Group" message={`Are you sure you want to delete "${deleteTarget?.profileName ?? ''}"?`} />}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
                {editor && (
                    <AttachmentGroupEditor
                        group={editor.group}
                        submitting={submitting}
                        onSubmitting={setSubmitting}
                        onSaved={handleSaved}
                        onCancel={() => setEditor(null)}
                    />
                )}
            </div>
        </main>
    );
};

export default AttachmentGroups;
